import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { Stack, Typography, Popover, useTheme, alpha } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { FormProvider, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { useRequestState } from '@app/hooks';
import { addCheckList } from '../slice';
import { selectAddCheckListState } from '../selector';

interface Props {
  open: HTMLElement | null;
  handleClose: () => void;
  cardId: number;
}

const AddCheckListPopover = ({ open, handleClose, cardId }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const methods = useForm();
  const id = open ? 'simple-popover' : undefined;
  const openPopver = Boolean(open);
  const checkListName = useWatch({ control: methods.control, name: 'checkListName' });
  const isDisabled = !checkListName?.trim();

  const handleSuccess = useCallback(() => {
    handleClose();
    methods.reset({});
  }, []);

  const { loading } = useRequestState({
    stateSelector: selectAddCheckListState,
    onSuccess: handleSuccess,
    errorShown: true,
  });

  const handleAddChecklist = () => {
    const trimmedName = checkListName?.trim();
    if (!trimmedName) return;
    dispatch(
      addCheckList({
        text: trimmedName,
        cardId,
        isCompleted: false,
      })
    );
  };

  return (
    <Popover
      id={id}
      open={openPopver}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: 3,
          backgroundColor: alpha(theme.palette.error.contrastText, 0.2),
          backdropFilter: 'blur(10px)',
          borderRadius: '10px',
          boxShadow: theme.shadows[5],
          gap: 1,
        },
      }}
    >
      <FormProvider methods={methods}>
        <Typography sx={{ textAlign: 'center', color: theme.palette.grey[700], fontWeight: 600 }}>
          {Strings.taskBoard.addChecklist}
        </Typography>
        <Stack>
          <RHFTextField
            name="checkListName"
            size="small"
            label={Strings.taskBoard.title}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddChecklist();
              }
            }}
          />
        </Stack>
        <Stack sx={{ mt: 1 }}>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={isDisabled}
            sx={{
              backgroundColor: theme.palette.taskboard.darkSlateGray,
              '&:hover': {
                backgroundColor: theme.palette.taskboard.darkSlateGray,
              },
            }}
            onClick={handleAddChecklist}
          >
            {Strings.button.add}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Popover>
  );
};

export { AddCheckListPopover };
